import {useState, useEffect, useCallback} from 'react'

import {Map} from 'immutable'
import PropTypes from 'prop-types'
import {useForm, FormProvider} from 'react-hook-form'
import {connect} from 'react-redux'

import {
  createResource as createResourceAction,
  readEndpoint as readEndpointAction,
  updateResource as updateResourceAction
} from 'redux-json-api'

import {
  Cancel,
  Continue,
  ControlButton
} from '../../../../../shared_components/ButtonTypes'

import Modal from '../../../../../shared_components/Modal'
import ShadowWrapper from '../../shared_firm/ShadowWrapper'
import ShadowHeader from '../../shared_firm/ShadowHeader'

import {
  useNotificationContext
} from '../../../../../shared_components/notifications/NotificationContext'

import {validHexColor} from '../../../../../lib/cobrandingTools'

import {
  findRawResourceByAttributeValue
} from '../../../../../lib/plan_data/userData'

import colors from '../../../../../dashboard/styles/abstracts/colors.module.scss'

import './editOrganizationCobranding.scss'

const COLOR_FIELDS = [
  {
    name: 'primary-color',
    label: 'Primary',
    defaultValue: colors.everplansPrimary,
    description: 'Primary Color: Use a dark shade that stands out against light backgrounds. Elements using this color will display white text for optimal readability.'
  },
  {
    name: 'secondary-color',
    label: 'Secondary',
    defaultValue: colors.everplansSecondary,
    description: 'Secondary Color: Choose a lighter tone that contrasts with dark backgrounds. Elements using this color will display dark text.'
  },
  {
    name: 'tertiary-color',
    label: 'Tertiary',
    defaultValue: colors.everplansTertiary,
    description: 'Tertiary Color: An accent color for highlights and emphasis.'
  }
]

export const EditOrganizationCobranding = ({
  createResource,
  organization,
  organizationCobranding,
  readEndpoint,
  updateResource
}) => {
  const {alwaysNotify} = useNotificationContext()

  const [isModalVisible, setIsModalVisible] = useState(false)
  const [processing, setProcessing] = useState(false)

  const cobrandingData = organizationCobranding || Map()
  const hasCobrandingRecord = !!organizationCobranding?.get('id')
  const persistedEnabled = organizationCobranding?.get('enabled') || false
  const [draftEnabled, setDraftEnabled] = useState(persistedEnabled)

  const getDefaultValues = useCallback(data => ({
    'primary-color': {value: data.get('primary-color') ||
      COLOR_FIELDS[0].defaultValue},
    'secondary-color': {value: data.get('secondary-color') ||
      COLOR_FIELDS[1].defaultValue},
    'tertiary-color': {value: data.get('tertiary-color') ||
      COLOR_FIELDS[2].defaultValue}
  }), [])

  const defaultValues = getDefaultValues(cobrandingData)
  const methods = useForm({defaultValues, mode: 'onBlur'})

  const watchedValues = methods.watch()

  const areSettingsUnchanged = () => {
    if (draftEnabled !== persistedEnabled) return false
    if (!draftEnabled) return true

    for (const key of Object.keys(defaultValues))
      if (watchedValues[key]?.value !== defaultValues[key]?.value) return false

    return true
  }

  const primaryColor = methods.watch('primary-color.value')
  const secondaryColor = methods.watch('secondary-color.value')
  const tertiaryColor = methods.watch('tertiary-color.value')
  const colorValues = {
    'primary-color': primaryColor,
    'secondary-color': secondaryColor,
    'tertiary-color': tertiaryColor
  }

  useEffect(() => {
    readEndpoint(
      `organizations/${organization.get('id')}?include=organization-cobranding`
    )
  }, [])

  useEffect(() => {
    if (organizationCobranding) {
      setDraftEnabled(persistedEnabled)
      methods.reset(getDefaultValues(organizationCobranding))
    } else {
      setDraftEnabled(false)
      methods.reset(getDefaultValues(Map()))
    }
  }, [getDefaultValues, methods, organizationCobranding, persistedEnabled])

  const organizationLogoUrl = organization.get('organization-logo')

  const toggleModal = useCallback(() => setIsModalVisible(prev => !prev), [])

  const resetState = useCallback(() => {
    setDraftEnabled(persistedEnabled)
    methods.reset(getDefaultValues(cobrandingData))
    setIsModalVisible(false)
  }, [cobrandingData, getDefaultValues, methods, persistedEnabled])

  const refreshOrganization = useCallback(() => {
    readEndpoint(
      `organizations/${organization.get('id')}?include=organization-cobranding`
    )
  }, [organization, readEndpoint])

  const handleSave = useCallback(() => {
    const hasErrors = methods.formState &&
      Object.keys(methods.formState.errors || {}).length > 0

    if (hasErrors) return

    setProcessing(true)

    const onError = () => {
      setProcessing(false)
      alwaysNotify.shortError('Failed to update organization cobranding.')
    }

    if (!draftEnabled && !hasCobrandingRecord) {
      setProcessing(false)
      setIsModalVisible(false)

      return
    }

    if (draftEnabled && !organizationLogoUrl) {
      setProcessing(false)

      alwaysNotify.shortError(
        'Organization must have a logo uploaded before enabling cobranding.'
      )

      return
    }

    const formValues = methods.getValues()
    const attributes = {
      enabled: draftEnabled,
      'primary-color': formValues['primary-color'].value,
      'secondary-color': formValues['secondary-color'].value,
      'tertiary-color': formValues['tertiary-color'].value,
      'organization-id': organization.get('id')
    }

    const onSuccess = () => {
      setProcessing(false)
      setIsModalVisible(false)

      alwaysNotify.shortSuccess('Organization cobranding has been updated.')

      refreshOrganization()
    }

    if (hasCobrandingRecord) {
      updateResource({
        type: 'organization-cobrandings',
        id: organizationCobranding.get('id'),
        attributes
      }).then(onSuccess).catch(onError)
    } else {
      createResource({
        type: 'organization-cobrandings',
        attributes
      }).then(onSuccess).catch(onError)
    }
  }, [
    alwaysNotify,
    draftEnabled,
    hasCobrandingRecord,
    methods,
    organization,
    organizationLogoUrl,
    refreshOrganization
  ])

  return (
    <div className='edit-form-controller edit-organization-cobranding'>
      <ShadowWrapper>
        <div className='settings-container flex-container'>
          <p>Status:</p>
          <div className='details'>
            <div className='status'>
              {persistedEnabled ? 'Enabled' : 'Disabled'}
            </div>
            <div className='description'>
              {persistedEnabled ?
                'Firms in this organization use the organization\'s cobranding.' :
                'Firms in this organization will not be able to use organization cobranding.'}
            </div>
            {!organizationLogoUrl && (
              <div className='warning-text'>
                Organization logo must be uploaded before cobranding can be
                enabled.
              </div>
            )}
            <div className='edit-config'>
              <ControlButton
                disabled={!organizationLogoUrl}
                faClass='fa-pencil-square-o'
                onClick={toggleModal}
                buttonText='Edit'
              />
              <Modal visible={isModalVisible}>
                <ShadowHeader
                  headerClassName='heading'
                  headerText='Organization Cobranding'
                />
                <div className='content'>
                  <div className='form-row'>
                    <p className='section-label'>Status:</p>
                    <div className='row-content'>
                      <div className='radio-group'>
                        <label className='radio-label'>
                          <input
                            type='radio'
                            name='org-cobranding-status'
                            checked={!draftEnabled}
                            onChange={() => setDraftEnabled(false)}
                          />
                          <span>Disabled</span>
                        </label>
                        <p className='field-description'>
                          Firms in this organization will not be able to use
                          organization cobranding.
                        </p>
                        <label className='radio-label'>
                          <input
                            type='radio'
                            name='org-cobranding-status'
                            checked={draftEnabled}
                            onChange={() => setDraftEnabled(true)}
                          />
                          <span>Enabled</span>
                        </label>
                        <p className='field-description'>
                          Firms in this organization use the organization's
                          cobranding.
                        </p>
                      </div>
                    </div>
                  </div>

                  {draftEnabled && (
                    <FormProvider {...methods}>
                      <div className='form-row colors-row'>
                        <p className='section-label'>Colors:</p>
                        <div className='row-content'>
                          <div className='colors-grid'>
                            {COLOR_FIELDS.map(field => (
                              <div key={field.name} className='color-column'>
                                <div className='color-label'>{field.label}</div>
                                <div className='color-input-group'>
                                  <input
                                    aria-label={`${field.label} color`}
                                    onChange={event => methods.setValue(
                                      `${field.name}.value`,
                                      event.target.value
                                    )}
                                    type='color'
                                    value={colorValues[field.name]}
                                  />
                                  <input
                                    aria-label={
                                      `${field.label} color hex value`
                                    }
                                    className='hex-input'
                                    type='text'
                                    {...methods.register(`${field.name}.value`, {
                                      pattern: {
                                        value: validHexColor,
                                        message: 'Must be a valid hex color'
                                      }
                                    })}
                                  />
                                </div>
                                {methods.formState.errors[field.name]?.value && (
                                  <span className='error'>
                                    {methods.formState.errors[field.name].value.message}
                                  </span>
                                )}
                                <p className='color-description'>
                                  {field.description}
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                      <div className='notes-section'>
                        <p>
                          Notes: Enabling organization cobranding will set all
                          firms in the organization to use organization
                          cobranding regardless of whether they previously
                          disabled cobranding or were Everplans Cobranded.
                          Disabling organization cobranding will revert firms
                          to their previous cobranding state.
                        </p>
                      </div>
                    </FormProvider>
                  )}
                </div>
                <div className='button-group'>
                  <Cancel onClick={resetState} />
                  <Continue
                    loading={processing}
                    onClick={handleSave}
                    disabled={areSettingsUnchanged() || processing}>
                    Save
                  </Continue>
                </div>
              </Modal>
            </div>
          </div>
        </div>
      </ShadowWrapper>
    </div>
  )
}

EditOrganizationCobranding.propTypes = {
  organization: PropTypes.instanceOf(Map).isRequired,
  organizationCobranding: PropTypes.instanceOf(Map),
  readEndpoint: PropTypes.func.isRequired,
  updateResource: PropTypes.func.isRequired,
  createResource: PropTypes.func.isRequired
}

const mapDispatchToProps = {
  readEndpoint: readEndpointAction,
  updateResource: updateResourceAction,
  createResource: createResourceAction
}

const mapStateToProps = ({api}, ownProps) => {
  const cobrandingResource = api['organization-cobrandings']

  const organizationCobranding = cobrandingResource ?
    findRawResourceByAttributeValue({
      attribute: 'organization-id',
      value: ownProps.organization.get('id'),
      rawResource: cobrandingResource
    }) :
    null

  return {organizationCobranding}
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(EditOrganizationCobranding)
